import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { needsScreener, wasScreenedOut } from "@/lib/screener";

export default function ProtectedRoute({ children, role, allowWaitlist = false }) {
    const { user } = useAuth();
    const location = useLocation();

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream">
                <div className="text-[#4C5551]">Loading…</div>
            </div>
        );
    }
    if (user === false) {
        // Stash the intended destination so the login flow can restore
        // it after auth. Router `state.from` gets dropped as soon as the
        // caregiver clicks "Sign in" on the landing page (fresh <Link>
        // navigation resets state), so we need a durable relay.
        // Used by Login.jsx `submit()` — read once + cleared after use.
        try {
            const fromUrl = `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
            // Only stash real deep-links, not the landing page itself.
            if (fromUrl && fromUrl !== "/" && !fromUrl.startsWith("/login")) {
                sessionStorage.setItem("mlcn_post_login_redirect", fromUrl);
            }
        } catch (_e) { /* private mode / storage disabled — best-effort */ }
        // Send them straight to the email-first sign-in (not the landing
        // page) — one step back into the app from any drip-email deep link.
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    if (role === "admin" && user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
    }
    if (!allowWaitlist && user.role !== "admin" && user.status !== "approved") {
        return <Navigate to="/pending" replace />;
    }
    // Tester pre-screener gate: any tester who hasn't completed the screener
    // is forced to /screener before they can see the rest of the app. Once
    // screened out, the screener page itself shows the "thank you" card.
    if (needsScreener(user) && location.pathname !== "/screener") {
        return <Navigate to="/screener" replace />;
    }
    // Screened-out testers shouldn't be able to navigate around the app —
    // pin them on /screener (which renders the thank-you / Prolific return
    // card) so they can't sneak into /dashboard, /chat, etc.
    if (wasScreenedOut(user) && location.pathname !== "/screener") {
        return <Navigate to="/screener" replace />;
    }
    return children;
}
